import { useParams } from 'react-router-dom'
import { useContext, useEffect, useState, useMemo } from 'react'
import { useGetExercises } from 'redux/customHooks/useGetExercises'
import { APIContext } from 'context/APIContext'
import { TrainingCalendarContext } from 'context/TrainingCalendarContext'
import { WeekOfTheYearKey } from 'classes/WeekOfTheYearKey'
import { GetProgramLevel } from 'functions/programLevelUtils'
import { default_equipment_profile /*, full_equipment_profile */ } from 'functions/equipmentUtils'
import moment from 'moment'

import ScreenFrame from 'components/Frame/ScreenFrame'
import ProgramPlaygroundHeader from 'components/Program/ProgramPlaygroundHeader'
import ContentFrame from 'components/Frame/ContentFrame'
import ProgramSimulatorSettings from 'components/Program/ProgramSimulatorSettings'
import CustomListBoxDropdown from 'components/Input/CustomListBoxDropdown'
import TrainingTimeline from 'components/Training/TrainingTimeline/TrainingTimeline'
import TrainingCalendar from 'components/Training/TrainingCalendar/TrainingCalendar'
import VolumeDisplayMode from 'components/Training/TrainingCalendar/VolumeDisplayMode'
import WorkoutScheduleDisplayMode from 'components/Training/TrainingCalendar/WorkoutScheduleDisplayMode'

const playgroundModeOptions = [
    { value: 'block', label: 'Monthly Calendar' },
    { value: 'week', label: 'Weekly Calendar' },
    { value: 'volume', label: 'Volume' },
    { value: 'workout-schedule', label: 'Workout Schedule' },
    { value: 'exercises', label: 'Exercises' }
]

const defaultARace = {
    id: 1,
    name: 'A Race',
    eventType: 'a-race',
    eventTrainingStartDate: moment().startOf('isoWeek').format('YYYY-MM-DD'),
    eventDate: moment().add(140, 'days').format('YYYY-MM-DD'),
    deloadAfterEvent: 1
}

export default function ProgramPlaygroundView() {

    const { urlProgramId } = useParams()

    const { exerciseHashMap } = useGetExercises()

    const { GetProgramMetadata, GetPreviewTrainingTimelineProgram, GetPreviewTrainingCalendar } = useContext(APIContext)

    const { trainingCalendarDateRange, UpdateTrainingCalendarTimescale } = useContext(TrainingCalendarContext)

    const clientDate = moment().local().format('YYYY-MM-DD')
    const clientWeekOfTheYearKey = useMemo(() => new WeekOfTheYearKey(moment(clientDate, 'YYYY-MM-DD')).toString(), [])
    const trainingTimelineStartDate = useMemo(() => moment().local().clone().subtract(32, 'months').startOf('isoWeek'), [])
    const trainingTimelineEndDate = useMemo(() => moment().local().clone().add(32, 'months').endOf('isoWeek').startOf('day'), [])

    const [programMetadata, setProgramMetadata] = useState(null)
    const [programLevelScore, setProgramLevelScore] = useState(0)
    const [kinesisEvents, setKinesisEvents] = useState([defaultARace])
    const [previewKinesisEvents, setPreviewKinesisEvents] = useState([]) // events the current preview was generated with. Minor addition used for the program length timeline
    const [volumeDetrainingModifier, setVolumeDetrainingModifier] = useState(0)
    const [assessmentJSON, setAssessmentJSON] = useState(null)
    const [injuries, setInjuries] = useState([])
    const [equipmentProfileJSON, setEquipmentProfileJSON] = useState(JSON.parse(JSON.stringify(default_equipment_profile)))
    const [workoutConfigWeekdayAssignmentsJSON, setWorkoutConfigWeekdayAssignmentsJSON] = useState([])
    const [workoutConfigWeekdayAssignmentOverridesJSON, setWorkoutConfigWeekdayAssignmentOverridesJSON] = useState([])
    const [generateLoading, setGenerateLoading] = useState(false)
    const [programPlaygroundMode, setProgramPlaygroundMode] = useState('block')
    const [selectedWeekOfTheYearKey, setSelectedWeekOfTheYearKey] = useState(clientWeekOfTheYearKey)
    const [programGenerationKey, setProgramGenerationKey] = useState(0)
    const [playgroundClientDate, setPlaygroundClientDate] = useState(moment().local().format('YYYY-MM-DD'))
    const [previewTrainingTimelinePrograms, setPreviewTrainingTimelinePrograms] = useState([])
    const [previewTrainingCalendarData, setPreviewTrainingCalendarData] = useState([])

    const InitializeProgramMetadataOnMount = async () => {
        setProgramMetadata(null)
        const newProgramMetadata = await GetProgramMetadata(urlProgramId)
        setProgramMetadata(newProgramMetadata)
        setWorkoutConfigWeekdayAssignmentsJSON([...(newProgramMetadata?.workoutConfigWeekdayAssignments ?? [])])
    }

    const HandleArrowKeyNavigation = (e) => {
        if (
            e.key !== 'ArrowLeft'
            && e.key !== 'ArrowUp'
            && e.key !== 'ArrowRight'
            && e.key !== 'ArrowDown'
        ) {
            return
        }

        const tagName = e.target?.tagName

        if (
            tagName === 'INPUT'
            || tagName === 'TEXTAREA'
            || tagName === 'SELECT'
            || e.target?.isContentEditable
        ) {
            return
        }

        e.preventDefault()

        const weekDelta = e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 1

        setSelectedWeekOfTheYearKey((currentWeekOfTheYearKey) => (new WeekOfTheYearKey(currentWeekOfTheYearKey).add(weekDelta).toString()))
    }

    const GenerateProgramVolumePreview = () => async () => {

        const eventTrainingStartDate = kinesisEvents.find((event) => event.eventType === 'a-race')?.eventTrainingStartDate ?? null

        if (eventTrainingStartDate === null) {
            return
        }

        setGenerateLoading(true)

        setPreviewTrainingTimelinePrograms([])
        setPreviewTrainingCalendarData([])

        const previewTrainingTimelineProgram = await GetPreviewTrainingTimelineProgram(urlProgramId, programLevelScore, kinesisEvents, volumeDetrainingModifier)

        // Once the new program is ready, focus the training timeline and calendar on its start week (all in one render so they move once).
        // The training calendar preview is then loaded by the program generation effect below

        setPlaygroundClientDate(eventTrainingStartDate)
        setSelectedWeekOfTheYearKey(new WeekOfTheYearKey(moment(eventTrainingStartDate, 'YYYY-MM-DD')).toString())
        setPreviewTrainingTimelinePrograms([previewTrainingTimelineProgram])
        setPreviewKinesisEvents(kinesisEvents)
        setProgramGenerationKey((currentProgramGenerationKey) => currentProgramGenerationKey + 1)

        setGenerateLoading(false)

    }

    const LoadTrainingCalendarPreviewData = async () => {
        if (previewTrainingTimelinePrograms.length === 0) {
            setPreviewTrainingCalendarData([])
            return
        }

        __LoadTrainingCalendarPreviewData()
    }

    const UpdateProgramPlaygroundMode = (mode) => {
        setProgramPlaygroundMode(mode)

        if (mode === 'block' || mode === 'week') {
            UpdateTrainingCalendarTimescale(mode)
        }

        if (mode === 'workout-schedule') {
            UpdateTrainingCalendarTimescale('week')
        }
    }

    const UpdateKinesisWorkoutCalendarItemDate = (draggedKinesisWorkout, newDate) => {

        const currentDate = moment(draggedKinesisWorkout.date, 'YYYY-MM-DD')
        const currentDateString = draggedKinesisWorkout.date
        const newDateString = newDate.format('YYYY-MM-DD')

        // Workouts can only be moved to another day within their current ISO week

        if (
            currentDateString === newDateString
            || currentDate.isSame(newDate, 'isoWeek') === false
        ) {
            return
        }

        const { workoutConfigId, workoutVariantLetter } = draggedKinesisWorkout

        // Store the override locally. One override per workout config variant per ISO week (same key the backend matches on)

        setWorkoutConfigWeekdayAssignmentOverridesJSON((currentOverrides) => [
            ...currentOverrides.filter((override) => !(
                override.workoutConfigId === workoutConfigId
                && override.workoutVariantLetter === workoutVariantLetter
                && moment(override.date, 'YYYY-MM-DD').isSame(currentDate, 'isoWeek')
            )),
            { workoutConfigId, workoutVariantLetter, date: newDateString }
        ])

        // Move the workout (and its schedule entry) on the calendar without reloading the preview

        setPreviewTrainingCalendarData((currentPreviewTrainingCalendarData) => {

            if (currentPreviewTrainingCalendarData === null) return currentPreviewTrainingCalendarData

            return currentPreviewTrainingCalendarData.map((item) => {

                const isDraggedKinesisWorkout = (
                    (item.calendarEventType === 'kinesis-workout' || item.calendarEventType === 'kinesis-scheduled-workout-data')
                    && item.workoutConfigId === workoutConfigId
                    && item.workoutVariantLetter === workoutVariantLetter
                    && item.date === currentDateString
                )

                if (!isDraggedKinesisWorkout) return item

                return { ...item, date: newDateString }
            })
        })
    }

    const __LoadTrainingCalendarPreviewData = async () => {

        setPreviewTrainingCalendarData(null)

        const calendarStartDate = trainingCalendarDateRange[0].format('YYYY-MM-DD')
        const calendarEndDate = trainingCalendarDateRange[trainingCalendarDateRange.length - 1].format('YYYY-MM-DD')

        const previewTrainingCalendarData = await GetPreviewTrainingCalendar(
            urlProgramId, programLevelScore, kinesisEvents, volumeDetrainingModifier,  
            assessmentJSON, injuries, equipmentProfileJSON, 
            workoutConfigWeekdayAssignmentsJSON, workoutConfigWeekdayAssignmentOverridesJSON, 
            calendarStartDate, calendarEndDate
        )
        
        setPreviewTrainingCalendarData(previewTrainingCalendarData)

    }

    useEffect(() => { InitializeProgramMetadataOnMount() }, [])
    useEffect(() => { UpdateTrainingCalendarTimescale(programPlaygroundMode) }, []) // The context is app wide and can still hold another page's timescale

    // Initial load of the training calendar preview after a program is generated. If the calendar is not on the start week yet,
    // its date range moves there and the calendar loads the preview itself, so only load here when it is already on the start week

    useEffect(() => {
        if (programGenerationKey === 0 || trainingCalendarDateRange.length === 0) return

        if (trainingCalendarDateRange[0].isSame(new WeekOfTheYearKey(selectedWeekOfTheYearKey).toDate(), 'isoWeek') === false) return

        __LoadTrainingCalendarPreviewData()
    }, [programGenerationKey])

    useEffect(() => {
        window.addEventListener('keydown', HandleArrowKeyNavigation)
        return () => window.removeEventListener('keydown', HandleArrowKeyNavigation)
    }, [])

    const selectedProgramLevel = useMemo(() => GetProgramLevel(programMetadata?.programLevels, programLevelScore), [programMetadata, programLevelScore])
    const playgroundClientWeekOfTheYearKey = useMemo(() => new WeekOfTheYearKey(moment(playgroundClientDate, 'YYYY-MM-DD')).toString(), [playgroundClientDate])

    if (
        Object.keys(exerciseHashMap).length === 0
        || programMetadata === null
    ) {
        return (
            <ScreenFrame>
                <div className='w-full h-20 bg-gray-200 rounded-lg animate-pulse' />
                <div className='w-full h-[800px] bg-gray-200 rounded-lg animate-pulse' />
            </ScreenFrame>
        )
    }

    return (
        <ScreenFrame>
            <ProgramPlaygroundHeader programMetadata={programMetadata} />

            <div className="grid min-h-0 flex-1 grid-cols-4 gap-3 2xl:gap-4">
                <div className="col-span-1 flex min-h-0 flex-col gap-3 2xl:gap-4">
                    <ProgramSimulatorSettings
                        programMetadata={programMetadata}
                        programLevelScore={programLevelScore}
                        setProgramLevelScore={setProgramLevelScore}
                        selectedProgramLevel={selectedProgramLevel}
                        kinesisEvents={kinesisEvents}
                        setKinesisEvents={setKinesisEvents}
                        volumeDetrainingModifier={volumeDetrainingModifier}
                        setVolumeDetrainingModifier={setVolumeDetrainingModifier}
                        assessmentJSON={assessmentJSON}
                        setAssessmentJSON={setAssessmentJSON}
                        injuries={injuries}
                        setInjuries={setInjuries}
                        equipmentProfileJSON={equipmentProfileJSON}
                        setEquipmentProfileJSON={setEquipmentProfileJSON}
                        workoutConfigWeekdayAssignmentsJSON={workoutConfigWeekdayAssignmentsJSON}
                        setWorkoutConfigWeekdayAssignmentsJSON={setWorkoutConfigWeekdayAssignmentsJSON}
                        workoutConfigWeekdayAssignmentOverridesJSON={workoutConfigWeekdayAssignmentOverridesJSON}
                        setWorkoutConfigWeekdayAssignmentOverridesJSON={setWorkoutConfigWeekdayAssignmentOverridesJSON}
                        OnGenerateButton={GenerateProgramVolumePreview}
                        generateLoading={generateLoading}
                    />

                    <div className='w-full space-y-2'>
                        <p className='text-sm'>Playground Mode</p>

                        <CustomListBoxDropdown
                            options={playgroundModeOptions}
                            value={programPlaygroundMode}
                            onChange={UpdateProgramPlaygroundMode}
                            shadow='shadow-md'
                        />
                    </div>
                </div>

                <div className="col-span-3 flex min-h-0 flex-col gap-3 2xl:gap-4">
                    <ContentFrame>
                        <TrainingTimeline
                            clientDate={playgroundClientDate}
                            clientWeekOfTheYearKey={playgroundClientWeekOfTheYearKey}
                            trainingTimelineStartDate={trainingTimelineStartDate}
                            trainingTimelineEndDate={trainingTimelineEndDate}
                            selectedWeekOfTheYearKey={selectedWeekOfTheYearKey}
                            setSelectedWeekOfTheYearKey={setSelectedWeekOfTheYearKey}
                            selectedWeekFocusKey={programGenerationKey}
                            trainingTimelinePrograms={previewTrainingTimelinePrograms}
                            kinesisEvents={previewKinesisEvents}
                        />
                    </ContentFrame>

                    {(programPlaygroundMode === 'block' || programPlaygroundMode === 'week') && (
                        <div className='h-[580px]'>
                            <TrainingCalendar
                                clientDate={playgroundClientDate}
                                trainingCalendarTimescale={programPlaygroundMode}
                                selectedWeekOfTheYearKey={selectedWeekOfTheYearKey}
                                setSelectedWeekOfTheYearKey={setSelectedWeekOfTheYearKey}
                                trainingCalendarData={previewTrainingTimelinePrograms === null ? [] : previewTrainingCalendarData}
                                trainingCalendarDateRange={trainingCalendarDateRange}
                                LoadTrainingCalendarData={LoadTrainingCalendarPreviewData}
                                UpdateKinesisWorkoutCalendarItemDate={UpdateKinesisWorkoutCalendarItemDate}
                            />
                        </div>
                    )}

                    {programPlaygroundMode === 'volume' && (
                        <VolumeDisplayMode
                            programMetadata={programMetadata}
                            compiledProgramVolume={previewTrainingTimelinePrograms[0]?.compiledProgramVolume ?? null}
                            selectedWeekOfTheYearKey={selectedWeekOfTheYearKey}
                        />
                    )}

                    {programPlaygroundMode === 'workout-schedule' && (
                        <div className='h-[580px]'>
                            <WorkoutScheduleDisplayMode 
                                programMetadata={programMetadata}
                                selectedWeekOfTheYearKey={selectedWeekOfTheYearKey}
                                trainingCalendarData={previewTrainingTimelinePrograms === null ? [] : previewTrainingCalendarData}
                                trainingCalendarDateRange={trainingCalendarDateRange}
                                LoadTrainingCalendarData={LoadTrainingCalendarPreviewData}
                            />
                        </div>
                    )}

                    {programPlaygroundMode === 'exercises' && (
                        <ContentFrame>
                            <div className='w-full p-4 text-center'>
                                Add exercises display here!!
                            </div>
                        </ContentFrame>
                    )}
                </div>
            </div>
        </ScreenFrame>
    )
}
